import { Controller, FieldPath, FieldValues, PathValue, useFormContext } from 'react-hook-form';
import { GroupBase, OptionsOrGroups } from 'react-select';
import ReactCreatableSelect from 'react-select/creatable';
import { formatCreateOptionLabel } from '../createOptionLabel';
import { FormFeedback } from '../FormFeedback';
import { FormField } from '../FormField';
import { getOptionFieldError } from '../formUtils';
import { FormFieldProps } from '../interfaces';
import { FormSelectInput, selectPortalProps, SelectStyles } from '../Select/Select';

interface Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends FormFieldProps<TFieldValues, TName> {
  onChange?: (e: unknown) => unknown;
  onCreateOption?: (inputValue: string) => void;
  formatCreateLabel?: (inputValue: string) => string;
  // [MCV-1628] Render the dropdown menu in a portal so it escapes ancestor
  // overflow/clip boundaries (e.g. FormDialog's `overflow-y-auto` body).
  // Defaults to true: an in-flow menu is positioned `absolute` inside that
  // scroll container, so it is both clipped by it and counted in its
  // scrollable overflow -- which is what put a ~300px phantom scrollbar on
  // every dialog holding a non-portaled select. Pass `usePortal={false}`
  // only for a menu that must stay in normal document flow.
  usePortal?: boolean;
  options: readonly (PathValue<TFieldValues, TName> | GroupBase<PathValue<TFieldValues, TName>>)[];
}

export const CreatableSelect = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: Props<TFieldValues, TName>
) => {
  const {
    name,
    description,
    disabled,
    cyTag,
    label,
    options,
    onChange,
    onCreateOption,
    formatCreateLabel = formatCreateOptionLabel,
    flagged,
    required,
    tooltip,
    usePortal = true,
    errorCyTag,
    reserveFeedbackSpace,
  } = props;
  const {
    formState: { errors },
  } = useFormContext();
  const error = getOptionFieldError(errors, name);
  const errorId = `${name}-error`;
  const helperId = `${name}-helper`;
  const resolvedErrorCyTag = errorCyTag ?? (cyTag ? `${cyTag}-error` : undefined);
  return (
    <Controller
      name={name}
      render={({ field }) => {
        return (
          <FormField
            id={name}
            label={label}
            tooltip={tooltip}
            required={required}
            feedback={
              <FormFeedback
                error={error?.message}
                errorId={errorId}
                errorCyTag={resolvedErrorCyTag}
                description={description}
                helperId={helperId}
                reserveSpace={reserveFeedbackSpace}
              />
            }
          >
            <div data-cy={cyTag}>
              <ReactCreatableSelect
                {...field}
                inputId={name}
                unstyled
                menuPortalTarget={usePortal ? selectPortalProps.menuPortalTarget : undefined}
                menuPosition={usePortal ? selectPortalProps.menuPosition : undefined}
                styles={{
                  singleValue: (base) => ({
                    ...base,
                    overflow: 'visible',
                  }),
                  ...(usePortal && { menuPortal: selectPortalProps.menuPortalStyle }),
                }}
                onChange={(option) => {
                  field.onChange(option);
                  onChange?.(option);
                }}
                onCreateOption={onCreateOption}
                formatCreateLabel={formatCreateLabel}
                isDisabled={disabled}
                aria-invalid={!!error || undefined}
                aria-required={required || undefined}
                aria-errormessage={error?.message ? errorId : undefined}
                aria-describedby={error?.message ? errorId : description ? helperId : undefined}
                components={{ Input: FormSelectInput }}
                classNames={SelectStyles(flagged, !!error)}
                options={
                  options as OptionsOrGroups<
                    PathValue<TFieldValues, TName>,
                    GroupBase<PathValue<TFieldValues, TName>>
                  >
                }
              />
            </div>
          </FormField>
        );
      }}
    />
  );
};
